import {
  computeGatepassExpiryDate,
  gatepassExpiryExceedsScValidity,
  parseDateValue,
  toStartOfDay,
} from "@features/gatepass/schema/gatepassScValidity.utils";
import { t, type TFunction } from "i18next";
import { z } from "zod";
import { optionalLettersWithSpaces, passportNumberSchema } from "./normalGatepassFormSchema";

const required = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const optional = (max: number, label = t("form-validation.field")) =>
  z.string().max(max, t("form-validation.max-length", { label, max })).optional();

const requiredPhone = (max: number, label = t("gatepass.phone-number")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .regex(/^\+[1-9]\d{6,14}$/, {
      message: t("form-validation.telephone", { label }),
    })
    .max(max, t("form-validation.max-length", { label, max }));

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t("form-validation.required", { label }),
      invalid_type_error: t("form-validation.required", { label }),
    }),
    z.string().min(1, t("form-validation.required", { label })),
  ]);

const TRANSLATION_KEY = "gatepass";

const offshoreEmiratesIdNumberSchema = (label: string) =>
  z
    .preprocess((val) => (val === null ? "" : val), z.string())
    .superRefine((val, ctx) => {
      const normalized = val.trim();
      if (!normalized) return;

      // Allow dashes in input (e.g. 784-1990-1234567-3) but validate by digits-only length.
      if (!/^[0-9-]+$/.test(normalized)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.invalid-number", { label }),
        });
        return;
      }

      const digitsOnly = normalized.replace(/\D/g, "");
      if (digitsOnly.length < 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.min-numbers", { min: 15 }),
        });
      }

      if (digitsOnly.length > 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-digits", { label, maxDigits: 15 }),
        });
      }
    })
    .optional();

export const getOffshoreGatepassFormSchema = (tFn: TFunction) =>
  z
    .object({
      SCStatus: z.string(),
      securityClearanceSerial: z.string().optional(),
      securityClearanceId: z.string().optional(),
      securityClearanceExpiryDate: z.string().optional(),
      securityClearanceValidity: z.string().optional(),
      securityClearanceFott: z.string().optional(),
      gatepassCategory: z.string(),
      passportNumber: passportNumberSchema(10, tFn(`${TRANSLATION_KEY}.passport-number`)),
      emiratesIdNumber: offshoreEmiratesIdNumberSchema(
        tFn(`${TRANSLATION_KEY}.emirates-id-number`),
      ),
      fullName: required(200, tFn(`${TRANSLATION_KEY}.fullName-en`)),
      nationality: optional(200, tFn(`${TRANSLATION_KEY}.nationality`)),
      passportNumberExpirationDate: optional(
        20,
        tFn(`${TRANSLATION_KEY}.passport-number-expiration-date`),
      ),
      phoneNumber: requiredPhone(200, tFn(`${TRANSLATION_KEY}.phone-number`)),
      entryPurpose: optional(500, tFn(`${TRANSLATION_KEY}.entry-purpose`)),
      sponsorCompany: required(200, tFn(`${TRANSLATION_KEY}.sponsor-company`)),
      expectedEntryDate: requiredDate(tFn(`${TRANSLATION_KEY}.expected-entry-date`)),
      port: required(200, tFn(`${TRANSLATION_KEY}.port`)),
      gate: required(200, tFn(`${TRANSLATION_KEY}.gate-selection`)),
      /** From security clearance only; always read-only in UI — optional when SC has no visa type. */
      visaType: optional(200, tFn(`${TRANSLATION_KEY}.visa-type`)),
      visitorCompanyEnglish: required(200, tFn(`${TRANSLATION_KEY}.visitor-company-name-en`)),
      visitorCompanyArabic: required(200, tFn(`${TRANSLATION_KEY}.visitor-company-name-ar`)),
      reference: optional(200, tFn(`${TRANSLATION_KEY}.reference`)),
      gatepassType: z.string(),
      vip: z.boolean(),
      requestFor: z.string(),
      // No preselected option: allow "" during editing, require selection in superRefine.
      gatepassDuration: z.union([z.enum(["daily", "weekly"]), z.literal("")]),
      permanentDuration: z.string().optional(),
      entryType: z.string(),
      FOTTEntry: z.boolean(),
      isFottDisabled: z.boolean(),
      registrationEmirate: optional(200, tFn(`${TRANSLATION_KEY}.registration-emirate`)),
      vehicleType: optional(200, tFn(`${TRANSLATION_KEY}.vehicle-type`)),
      vehicleColour: optionalLettersWithSpaces(200, tFn(`${TRANSLATION_KEY}.vehicle-colour`)),
      category: optional(200, tFn(`${TRANSLATION_KEY}.category`)),
      plateNumber: optional(50, tFn(`${TRANSLATION_KEY}.vehicle-plate-number`)),
      hasCamera: z.boolean(),
      cameraSerialNumber: optional(20, tFn(`${TRANSLATION_KEY}.camera-serial-number`)),
      cameraModel: optional(20, tFn(`${TRANSLATION_KEY}.camera-model`)),
      vessels: z.array(
        z.object({
          vesselName: optional(200, tFn(`${TRANSLATION_KEY}.vessel-name`)),
          vesselImoNumber: optional(8, tFn(`${TRANSLATION_KEY}.vessel-imo-number`)),
          noImoNumber: z.boolean().optional(),
        }),
      ),
      locations: z.array(
        z.object({
          location: z.string(),
          entryPurpose: z.string(),
        }),
      ),
      groupMembers: z.array(z.any()),
      photoUrl: optional(200, tFn(`${TRANSLATION_KEY}.photoUrl`)),
    })
    .superRefine((data, ctx) => {
      if (!data.gatepassDuration) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.gatepass-duration`),
          }),
          path: ["gatepassDuration"],
        });
      }

      const hasPassportNumber = Boolean(data.passportNumber?.trim());
      const hasEmiratesIdNumber = Boolean(data.emiratesIdNumber?.trim());
      if (!hasPassportNumber && !hasEmiratesIdNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.passport-number`),
          }),
          path: ["passportNumber"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.emirates-id-number`),
          }),
          path: ["emiratesIdNumber"],
        });
      }
      if ((hasPassportNumber || hasEmiratesIdNumber) && !data.securityClearanceId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.notifications.security-clearance-not-found`),
          path: ["securityClearanceId"],
        });
      }

      if (data.vehicleType?.trim() && !data.registrationEmirate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.registration-emirate`),
          }),
          path: ["registrationEmirate"],
        });
      }
      if (data.vehicleType?.trim() && !data.plateNumber?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.vehicle-plate-number`),
          }),
          path: ["plateNumber"],
        });
      }

      if (data.hasCamera) {
        const serial = data.cameraSerialNumber?.trim() ?? "";
        const model = data.cameraModel?.trim() ?? "";
        const cameraSerialRegex = /^[A-Za-z0-9-\s]+$/;
        const cameraModelRegex = /^[A-Za-z0-9,.;\-\s]+$/;
        if (!serial) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.camera-serial-number`),
            }),
            path: ["cameraSerialNumber"],
          });
        } else if (!cameraSerialRegex.test(serial)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.camera-serial-number-validation`, {
              defaultValue: "Camera serial number can only contain letters, numbers, spaces, and -",
            }),
            path: ["cameraSerialNumber"],
          });
        }
        if (!model) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.camera-model`),
            }),
            path: ["cameraModel"],
          });
        } else if (!cameraModelRegex.test(model)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.camera-model-validation`, {
              defaultValue:
                "Camera model can only contain letters, numbers, spaces, and the symbols - , . ;",
            }),
            path: ["cameraModel"],
          });
        }
      }

      if (!data.vessels.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.vessel-name`),
          }),
          path: ["vessels"],
        });
      }
      data.vessels.forEach((vessel, index) => {
        const name = vessel.vesselName?.trim() ?? "";
        const imo = vessel.vesselImoNumber?.trim() ?? "";
        const noImo = vessel.noImoNumber === true;

        if (!noImo && !imo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.vessel-imo-number`),
            }),
            path: ["vessels", index, "vesselImoNumber"],
          });
        }
        if (!noImo && imo && !/^\d+$/.test(imo)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.invalid-number", {
              label: tFn(`${TRANSLATION_KEY}.vessel-imo-number`),
            }),
            path: ["vessels", index, "vesselImoNumber"],
          });
        }
        if (!noImo && imo && imo.length > 8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.max-length", {
              label: tFn(`${TRANSLATION_KEY}.vessel-imo-number`),
              max: 8,
            }),
            path: ["vessels", index, "vesselImoNumber"],
          });
        }
        if (!name) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.vessel-name`),
            }),
            path: ["vessels", index, "vesselName"],
          });
        }
      });

      const seenVesselNames = new Map<string, number>();
      data.vessels.forEach((vessel, index) => {
        const name = vessel.vesselName?.trim() ?? "";
        if (!name) {
          return;
        }
        const key = name.toLowerCase();
        if (seenVesselNames.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.duplicate-vessel-name`),
            path: ["vessels", index, "vesselName"],
          });
          return;
        }
        seenVesselNames.set(key, index);
      });

      const seenImoNumbers = new Map<string, number>();
      data.vessels.forEach((vessel, index) => {
        const noImo = vessel.noImoNumber === true;
        const imo = vessel.vesselImoNumber?.trim() ?? "";
        if (noImo || !imo) {
          return;
        }
        if (seenImoNumbers.has(imo)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.duplicate-vessel-imo`),
            path: ["vessels", index, "vesselImoNumber"],
          });
          return;
        }
        seenImoNumbers.set(imo, index);
      });

      const parsedExpectedEntryDate = parseDateValue(data.expectedEntryDate);
      if (parsedExpectedEntryDate) {
        const selectedDate = toStartOfDay(parsedExpectedEntryDate);
        const today = toStartOfDay(new Date());

        if (selectedDate.getTime() < today.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-past", {
              label: t(`${TRANSLATION_KEY}.expected-entry-date`),
            }),
            path: ["expectedEntryDate"],
          });
        }
      }

      const gatepassExpiryDate = computeGatepassExpiryDate(
        data.expectedEntryDate,
        data.gatepassDuration,
        data.permanentDuration,
      );
      if (gatepassExpiryExceedsScValidity(gatepassExpiryDate, data.securityClearanceValidity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.gatepass-exceeds-security-clearance-expiry`),
          path: ["securityClearanceValidity"],
        });
      }
    });

export type OffshoreGatepassFormData = z.infer<ReturnType<typeof getOffshoreGatepassFormSchema>>;
